"use client";

import { useEffect, useState } from "react";
import { cn, scrollToId } from "@/lib/utils";

export interface SectionNavItem {
  id: string;
  label: string;
}

interface Props {
  sections: SectionNavItem[];
  /** Passed to scrollToId on click — defaults to 160 to clear the sticky
   *  header plus a SectionHeader's eyebrow label sitting above its heading. */
  scrollOffset?: number;
}

/* A sticky horizontal jump-nav for long, section-stacked pages (landing-style
   full-bleed sections — card grids, tables), rather than a sidebar TOC: a side
   rail would force every section into a narrow column. A top bar needs no
   layout change to the sections themselves. */
export default function SectionNav({ sections, scrollOffset = 160 }: Props) {
  const [activeId, setActiveId] = useState<string>(sections[0]?.id ?? "");

  useEffect(() => {
    const observers: IntersectionObserver[] = [];

    sections.forEach(({ id }) => {
      const el = document.getElementById(id);
      if (!el) return;
      const obs = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) setActiveId(id);
        },
        { rootMargin: "-20% 0px -70% 0px", threshold: 0 }
      );
      obs.observe(el);
      observers.push(obs);
    });

    return () => observers.forEach((o) => o.disconnect());
  }, [sections]);

  return (
    <nav
      aria-label="Section navigation"
      className="border-border bg-background/90 sticky top-[var(--header-height)] z-10 hidden border-b backdrop-blur md:block"
    >
      <div
        className="mx-auto flex max-w-7xl gap-1 overflow-x-auto px-4 sm:px-6 lg:px-8"
        style={{ scrollbarWidth: "none" }}
      >
        {sections.map((section) => {
          const isActive = activeId === section.id;
          return (
            <a
              key={section.id}
              href={`#${section.id}`}
              onClick={(event) => {
                event.preventDefault();
                setActiveId(section.id);
                scrollToId(section.id, scrollOffset);
              }}
              className={cn(
                "shrink-0 border-b-2 px-3 py-3 text-sm font-medium whitespace-nowrap transition-colors",
                isActive
                  ? "border-brand-green text-foreground"
                  : "text-muted-foreground hover:text-foreground border-transparent"
              )}
            >
              {section.label}
            </a>
          );
        })}
      </div>
    </nav>
  );
}
